import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  CssBaseline,
  Alert,
  IconButton,
  LinearProgress,
  MenuItem,
} from "@mui/material";
import {
  ContentBox,
  StyledButton,
  TitleText,
  PageContainer,
} from "./Default.js";
import axios from "axios";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import RelatedTools from "../Components/RelatedTools.js";
import { message } from "../Constants/message.js";
import CreditsBanner from "../Components/CreditsNudge";
import JsonFormatterContent from "../Contents/JSONFormatterContent.js";
import CommentSection from "../Components/CommentSection.js";

export default function JsonFormatterTool({ user, login, isLoggedIn }) {
  const [jsonInput, setJsonInput] = useState("");
  const [formattedJson, setFormattedJson] = useState("");
  const [indentType, setIndentType] = useState("2");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("jsonFormatterInput");
    if (saved) setJsonInput(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem("jsonFormatterInput", jsonInput);
  }, [jsonInput]);

  const formatJson = async () => {
    setSuccess("");
    setError("");
  
    const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
    const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";
    const guestUsedKey = `${toolKey}_guestUsed`;
    const guestUsed = localStorage.getItem(guestUsedKey);
  
    if (!isLoggedIn && guestUsed) {
      setError(message.loginPrompt);
      setTimeout(() => login(), 1500);
      return;
    }
  
    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      if (credits.remaining < 1) {
        setError("Insufficient credits to format JSON. Please upgrade your plan.");
        return;
      }
    }
  
    setLoading(true);
  
    try {
      const parsed = JSON.parse(jsonInput);
      let space;
      switch (indentType) {
        case "2":
          space = 2;
          break;
        case "4":
          space = 4;
          break;
        case "tab":
          space = "\t";
          break;
        case "minified":
          space = 0;
          break;
        default:
          space = 2;
      }
  
      const output = JSON.stringify(parsed, null, space);
      setFormattedJson(output);
  
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
          { toolName: "json_formatter" }
        );
        setCredits({
          remaining: res.data.remaining_credits || 0,
          availed: res.data.availed_credits || 0,
          total: res.data.total_credits || 0,
        });
      } else if (isLoggedIn && user.account_type.toLowerCase() === "paid") {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
          { toolName: "json_formatter" }
        );
      }
  
      if (!isLoggedIn && !guestUsed) {
        localStorage.setItem(guestUsedKey, "true");
      }
  
      setSuccess("JSON formatted successfully!");
    } catch (e) {
      setError("Invalid JSON format.");
      setFormattedJson("");
    } finally {
      setLoading(false);
    }
  };
  

  const handleCopy = () => {
    navigator.clipboard.writeText(formattedJson);
    setSuccess("Formatted JSON copied to clipboard!");
  };

  const handleDownload = () => {
    const blob = new Blob([formattedJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "formatted.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const relatedTools = [
    { name: "CSV to JSON", path: "/csv-to-json", emoji: "🔁" },
    { name: "JSON to CSV", path: "/json-to-csv", emoji: "📄" },
    {
      name: "Random Email Generator",
      path: "/random-email-generator",
      emoji: "📧",
    },
  ];

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 2 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            JSON Formatter 📚
          </TitleText>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ mt: 1, color: "text.secondary" }}
          >
            Validate and beautify your JSON data with different indentation
            options.
          </Typography>

          {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              mt={2}
            >
              <b>⚠️ Free Plan:</b> Each formatting consumes 1 credit. Upgrade to
              a Paid plan for unlimited access.
            </Typography>
          ) : (
            isLoggedIn && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>🎁 Paid Plan:</b> Unlimited JSON formatting with no credit
                deduction.
              </Typography>
            )
          )}

          {loading && <LinearProgress sx={{ mt: 2, mb: 1 }} />}
          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess("")}
              sx={{ mt: 2 }}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Horizontal Layout: Input | CTA | Output */}
          <Box
            mt={3}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
          >
            {/* Input */}
            <Box flex={1}>
              <Typography variant="h5" mb={1} fontWeight="bold">
                JSON Input
              </Typography>
              <TextField
                multiline
                minRows={20}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`{ "name": "John", "age": 30 }`}
                variant="outlined"
                fullWidth
              />
            </Box>

            {/* CTA & Dropdown */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{ minWidth: "80px" }}
            >
              <TextField
                select
                label="Indentation"
                size="small"
                value={indentType}
                onChange={(e) => setIndentType(e.target.value)}
                fullWidth
              >
                <MenuItem value="2">2 Spaces</MenuItem>
                <MenuItem value="4">4 Spaces</MenuItem>
                <MenuItem value="tab">Tab</MenuItem>
                <MenuItem value="minified">Minified</MenuItem>
              </TextField>

              <StyledButton
                variant="contained"
                color="success"
                onClick={formatJson}
                fullWidth
              >
                Format JSON
              </StyledButton>
            </Box>

            {/* Output */}
            <Box flex={1}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h5" fontWeight="bold">
                  Formatted Output
                </Typography>
                {formattedJson && (
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleCopy}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleDownload}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <TextField
                multiline
                minRows={20}
                value={formattedJson}
                InputProps={{ readOnly: true }}
                variant="outlined"
                fullWidth
              />
            </Box>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="json-formatter-online"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <RelatedTools relatedTools={relatedTools} />
        <JsonFormatterContent />
      </PageContainer>
    </>
  );
}
